import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { performSearch } from '../search/thunks/search.thunk';
import { selectResults } from '../search/store/search.selector';
import { setBlockedCount } from '../search/store/search.store';
import { SearchResult } from '../search/interfaces/search.interface';
import '../search/search-results';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { booleanConverter } from '../../common/converters';
import { appendSchemaToHead } from '../../common/utils/seo-schema-utils';
import './product-cards-pagination';
import { getProductUrl } from './utils/product-card.util';
import { mapProductCardData } from '../search/mapping/search-product-card.mapping';
import "./variations/he-product-card.display";
import { CtaTypeLabel } from './product.interface';
import ProductCardSearchUtil from './utils/product-card.search.util';
import { bs5Connect } from '../../common/bs5Utils/bs5.mixin';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import emit from '../../common/events';

enum ComponentType {
  shared = 'shared',
  isolated = 'isolated',
}

@customElement('mhecomm-he-product-cards')
export class ProductCards extends connect(store)(bs5Connect(BaseComponent)) {
  @property()
  cardType?: string;

  @property()
  businessUnit?: string;

  @property({ converter: booleanConverter })
  limitCard?: boolean;

  @property({ converter: booleanConverter })
  lazyLoadImage?: boolean = true;

  @property({ type: Number })
  numberofcards?: number;

  @property({ type: Number })
  cardsPerPageDesktop = 4;

  @property({ type: Number })
  cardsPerPageMobile = 2;

  @property({ converter: booleanConverter })
  collapse = false;

  @property()
  disciplineName?: string;

  @property()
  showMoreText = 'Show All';

  @property()
  showLessText = 'Hide All';

  @property()
  orientation = "vertical";

  @property()
  cta = CtaTypeLabel.NO_CTA;

  @property({ converter: booleanConverter })
  showCopyright = false;

  @property({ converter: booleanConverter })
  showEdition = false;

  @property({ converter: booleanConverter })
  showAuthors = false;

  @property({ converter: booleanConverter })
  showISBN = false;

  @property({ converter: booleanConverter })
  showBusinessUnitLabel = false;

  @property({ converter: booleanConverter })
  showProductType = false;

  @property({ converter: booleanConverter })
  isEditorMode?: boolean;

  @property({ type: Array })
  identifiers?: Array<string>;

  @property({ type: Array })
  featuredItems?: Array<{ product: string; position: string }>;

  @property({ type: Array })
  productBlockList?: Array<string>;

  @property()
  authoredId = 'he-product-cards';

  @property()
  componentType?: ComponentType = ComponentType.isolated;

  @property()
  categoryId?: string;

  @property()
  groupAriaLabel?: string;

  pageSize = 10;

  @state()
  private results: Array<SearchResult> = [];

  @state()
  private start = 1;

  @state()
  private total = 0;

  @state()
  private showAllCards = false;

  @state()
  private collapseInstance: any;

  @property()
  sorting?: string;

  @property({ type: String })
  headingLevel = "h3";

  connectedCallback() {
    super.connectedCallback();
    // Set pageSize based on collapse mode
    if (this.collapse) {
      this.pageSize = 500;
    }
    this._search(this.start);
  }

  private getCurrentCardsPerPage(): number {
    // Bootstrap mobile breakpoint is 576px (sm breakpoint)
    return window.innerWidth < 576 ? this.cardsPerPageMobile : this.cardsPerPageDesktop;
  }

  private async _search(page: number) {
    const searchParameters = {
      hits: true,
      searchTerms: '',
      isbnList: this.identifiers,
      catalog: 'HE',
      start: page,
      facets: false,
      pageLength: this.pageSize,
      categoryId: this.categoryId,
      forceIsbnSearch: true
    };
    if (ProductCardSearchUtil.isSortAlphabetically(this.sorting)) {
      searchParameters['sortBy'] = "title_asc";
    }
    const response = await store.dispatch(
      performSearch(searchParameters,false)
    );
    this.start = page;
    this.total = response?.total || 0;
    if (this.componentType === ComponentType.isolated) {
      this.results = response?.hits || [];
    }
  }

  stateChanged(state: StoreState) {
    if (this.componentType === ComponentType.shared) {
      this.results = selectResults(state) || [];
    }
  }

  updated(changedProperties: any) {
    super.updated(changedProperties);
    
    // Update blocked count in Redux store when productBlockList changes
    if (changedProperties.has('productBlockList')) {
      const blockedCount = this.productBlockList?.length || 0;
      store.dispatch(setBlockedCount(blockedCount));
    }
    
    // Initialize Bootstrap collapse if collapse mode is enabled
    if (this.collapse) {
      const collapseId = `#collapse-cards-${this.authoredId}`;
      this.collapseInstance = this.bsInit(collapseId, window.bootstrap?.Collapse, { toggle: false })?.instance;
    }
  }

  getSchema(results: SearchResult[]) {
    return {
      '@context': 'http://schema.org',
      '@type': 'ItemList',
      numberOfItems: this.total,
      itemListElement: results.map((result, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'ProductCollection',
            image: this.getCardImageUrl(result.document),
            name: result.document.title,
            description: result.document.description,
            url: getProductUrl(result.document),
          },
        };
      }),
    };
  }

  getCardImageUrl(product) {
    switch (product.type) {
      case 'HEDocument':
        return getImageSrcUrl(product?.isbn13 || product?.isbn);
    }
    return '';
  }

  private prepareProductCardsList() {
    const limitedResults =
      this.limitCard && this.numberofcards ? this.results.slice(0, this.numberofcards) : this.results;
    
    appendSchemaToHead(this.getSchema(limitedResults), this.authoredId);
    
    let productCardsList = mapProductCardData(limitedResults, "HE");
    
    // Apply product blocking - remove blocked products
    if (this.productBlockList && this.productBlockList.length > 0) {
      productCardsList = productCardsList.filter(card => 
        !this.productBlockList!.includes(this.getProductId(card))
      );
    }
    
    // Apply manual sorting if configured
    if (ProductCardSearchUtil.isSortManually(this.sorting)) {
      ProductCardSearchUtil.sortResultsByManualType(productCardsList, this.identifiers);
    }
    
    // Apply featured items positioning
    if (this.featuredItems && this.featuredItems.length > 0) {
      productCardsList = this.applyFeaturedItemsPositioning(productCardsList);
    }
    
    return productCardsList;
  }

  private getProductId(card: any): string {
    return card.isbn || card.id;
  }

  private applyFeaturedItemsPositioning(productCardsList: any[]): any[] {
    const result = [...productCardsList];
    
    // Sort featured items by position to process them in order
    const sortedFeaturedItems = [...(this.featuredItems || [])]
      .sort((a, b) => parseInt(a.position) - parseInt(b.position));
    
    for (const featuredItem of sortedFeaturedItems) {
      const targetPosition = parseInt(featuredItem.position) - 1; // Convert to 0-based index
      
      // Skip invalid positions
      if (targetPosition < 0) continue;
      
      // Find the featured product in the current list
      const featuredProductIndex = result.findIndex(card => 
        this.getProductId(card) === featuredItem.product
      );
      
      if (featuredProductIndex !== -1) {
        // Remove the product from its current position
        const [featuredProduct] = result.splice(featuredProductIndex, 1);
        
        // Insert it at the target position (ensuring we don't exceed array bounds)
        const insertPosition = Math.min(targetPosition, result.length);
        result.splice(insertPosition, 0, featuredProduct);
      }
    }
    
    return result;
  }

  private renderProductCard(item: any) {
    return html`<mhecomm-he-product-card-display
      .product='${item}'
      cta="${this.cta}"
      .showBusinessUnitLabel="${this.showBusinessUnitLabel}"
      .showBusinessLabelType="${this.showProductType}"
      .showCopyright="${this.showCopyright}"
      .showEdition="${this.showEdition}"
      .showAuthors="${this.showAuthors}"
      .showISBN="${this.showISBN}"
      orientation="${this.orientation}"
      headingLevel="${this.headingLevel}">
    </mhecomm-he-product-card-display>`;
  }

  private renderShowMoreButton(hasMoreCards: boolean, collapseId: string) {
    if (!hasMoreCards) return nothing;
    
    // Build the button text dynamically
    const baseText = this.showAllCards ? this.showLessText : this.showMoreText;
    const disciplineText = this.disciplineName ? ` ${this.disciplineName}` : '';
    const buttonText = `${baseText}${disciplineText}`;
    
    return html`
      <div class="d-flex justify-content-center mt-3">
        <button 
          class="btn link-primary" 
          type="button" 
          data-bs-toggle="collapse" 
          data-bs-target="#${collapseId}" 
          aria-expanded="${this.showAllCards}"
          aria-controls="${collapseId}"
          aria-label="${buttonText} product cards"
          @click=${this.handleShowMoreClick}>
          ${buttonText}
          ${this.uxtIconReady 
            ? html`<uxt-icon 
                class="ms-1" 
                icon="${this.showAllCards ? 'chevron-up' : 'chevron-down'}"
                aria-hidden="true">
              </uxt-icon>` 
            : nothing}
        </button>
      </div>
    `;
  }

  private handleShowMoreClick = () => {
    this.emitCollapseAnalytics(this.showAllCards);
    this.showAllCards = !this.showAllCards;
    // Use the Bootstrap collapse instance for programmatic control
    if (this.collapseInstance) {
      this.collapseInstance.toggle();
    }
  }

  private getAriaLabelForList(): string | undefined {
    if (!this.groupAriaLabel) return undefined;
    return this.groupAriaLabel;
  }

  private emitCollapseAnalytics(isCurrentlyExpanded: boolean) {
    const disciplineText = this.disciplineName ? ` ${this.disciplineName}` : '';
    const action = isCurrentlyExpanded ? 'Hide' : 'Show';
    const label = `${action} all${disciplineText} products`;
    
    emit({
      element: this,
      name: 'click',
      detail: {
        comp: 'collapse',
        label: label,
        id: this.authoredId,
        expanded: isCurrentlyExpanded.toString(),
        area: 'main'
      },
      dataLayer: true
    });
  }

  private renderHeaderFooterSlots(showSlots: boolean) {
    const header = showSlots ? html`<slot name="header-parsys"></slot>` : nothing;
    const footer = showSlots ? html`<slot name="footer-parsys"></slot>` : nothing;
    return { header, footer };
  }

  private renderPagination() {
    return html`
      <mhecomm-product-cards-pagination
        start="${this.start}"
        total="${this.total}"
        @onPageChange=${(e) => this._search(e.detail?.page)}>
      </mhecomm-product-cards-pagination>
    `;
  }

  private renderProductCardsList(cards: any[]) {
      return html`
        <ul aria-label="${ifDefined(this.getAriaLabelForList())}" class="product-card-row-${this.orientation} mb-3 list-unstyled">
          ${cards.map((item) => html`<li>${this.renderProductCard(item)}</li>`) }
        </ul>
      `;
  }

  private renderOriginalFlow(productCardsList: any[]) {
    const { header, footer } = this.renderHeaderFooterSlots(
      this.isEditorMode || productCardsList.length > 0
    );

    return html`
      ${header}
      <div class="product-cards-container">
        ${this.renderProductCardsList(productCardsList)}
      </div>
      ${this.renderPagination()}
      ${footer}
    `;
  }

  private renderCollapseFlow(productCardsList: any[]) {
    // Use responsive cards per page based on viewport
    const cardsPerPage = this.getCurrentCardsPerPage();
    const hasMoreCards = productCardsList.length > cardsPerPage;
    
    const initialCards = productCardsList.slice(0, cardsPerPage);
    const additionalCards = hasMoreCards ? productCardsList.slice(cardsPerPage) : [];
    const collapseId = `collapse-cards-${this.authoredId}`;

    const { header, footer } = this.renderHeaderFooterSlots(
      this.isEditorMode || productCardsList.length > 0
    );

    return html`
      ${header}
      <div class="product-cards-container">
        <!-- Initial Cards -->
        ${this.renderProductCardsList(initialCards)}
        
        <!-- Additional Cards (Collapsible) -->
        ${hasMoreCards ? html`
          <div class="collapse" id="${collapseId}">
            ${this.renderProductCardsList(additionalCards)}
          </div>
        ` : nothing}
        
        <!-- Show More/Less Button -->
        ${this.renderShowMoreButton(hasMoreCards, collapseId)}
      </div>
      ${footer}
    `;
  }

  renderComp() {
    const productCardsList = this.prepareProductCardsList();
    
    return this.collapse 
      ? this.renderCollapseFlow(productCardsList)
      : this.renderOriginalFlow(productCardsList);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-he-product-cards': ProductCards;
  }
}
